// Copyright 2026. WebPros International GmbH. All rights reserved.

import { HighAvailabilityEntityStatus } from 'common/api/resources/HighAvailability';
import React from 'react';
import {
    Action,
    Icon,
    Text,
    Translate,
} from '@plesk/ui-library';
import {
    ICONS,
    INTENT_TYPE,
} from 'common/constants';
import { Tooltip } from 'common/components';
import { COLORS } from 'common/theme';
import { ReactComponent as HaCancelledIcon } from 'common/icons/rounded-slash.svg';
import { ReactComponent as HaPendingIcon } from 'common/icons/3dots-arrow-right-filled.svg';
import { ReactComponent as HaPendingMoveBackIcon } from 'common/icons/3dots-arrow-left-filled.svg';
import { ReactComponent as HaEvacuatedIcon } from 'common/icons/rounded-arrow-right.svg';
import { ReactComponent as HaMovedBackIcon } from 'common/icons/rounded-arrow-left.svg';
import { ReactComponent as HaNotMovedBackIcon } from 'common/icons/right-line.svg';

export interface IHighAvailabilityEntityStatusProps {
    status: HighAvailabilityEntityStatus;
    progress?: number;
    moveBackProgress?: number;
    isMoveBackPending?: boolean;
    isMoveBackFailed?: boolean;
    isMoveBackDone?: boolean;
    failReason?: string | null;
    compact?: boolean;
}

interface IStatusLabelProps {
    icon: React.ReactNode;
    label: React.ReactNode;
    subtitle?: React.ReactNode;
}

const StatusLabel: React.FC<IStatusLabelProps> = ({ icon, label, subtitle }) => (
    <span style={{ display: 'inline-flex', alignItems: subtitle !== undefined ? 'flex-start' : 'center', gap: '4px' }}>
        {icon}
        {subtitle !== undefined ? (
            <span>
                <div>{label}</div>
                <Text intent={INTENT_TYPE.MUTED} fontSize="sm">{subtitle}</Text>
            </span>
        ) : label}
    </span>
);

export const HighAvailabilityEntityStatusComponent: React.FC<IHighAvailabilityEntityStatusProps> = ({
    status,
    progress,
    moveBackProgress,
    isMoveBackPending,
    isMoveBackFailed,
    isMoveBackDone,
    failReason,
}) => {
    if (status === HighAvailabilityEntityStatus.RUNNING) {
        return (
            <StatusLabel
                icon={<Action progress={true} />}
                label={<Translate content="highAvailability.activities.processes.status.running" />}
                subtitle={progress !== undefined ? `${progress}%` : undefined}
            />
        );
    }

    if (status === HighAvailabilityEntityStatus.COMPLETED) {
        if (isMoveBackPending) {
            return (
                <StatusLabel
                    icon={<span style={{ color: COLORS.INFO }}><HaPendingMoveBackIcon width={15} height={15} /></span>}
                    label={<Translate content="highAvailability.activities.processes.status.pendingMoveBack" />}
                    subtitle={<Translate content="highAvailability.activities.processes.status.pendingMoveBackSubtitle" />}
                />
            );
        }

        if (moveBackProgress !== undefined) {
            return (
                <StatusLabel
                    icon={<Action progress={true} />}
                    label={<Translate content="highAvailability.activities.processes.status.movingBack" />}
                    subtitle={`${moveBackProgress}%`}
                />
            );
        }

        if (isMoveBackFailed) {
            return (
                <StatusLabel
                    icon={<span style={{ color: COLORS.DANGER }}><HaNotMovedBackIcon width={15} height={15} /></span>}
                    label={<Translate content="highAvailability.activities.processes.status.notMovedBack" />}
                />
            );
        }

        if (isMoveBackDone) {
            return (
                <StatusLabel
                    icon={<span style={{ color: COLORS.SUCCESS }}><HaMovedBackIcon width={15} height={15} /></span>}
                    label={<Translate content="highAvailability.activities.processes.status.movedBack" />}
                />
            );
        }

        return (
            <StatusLabel
                icon={<span style={{ color: COLORS.SUCCESS }}><HaEvacuatedIcon width={15} height={15} /></span>}
                label={<Translate content="highAvailability.activities.processes.status.evacuated" />}
            />
        );
    }

    if (status === HighAvailabilityEntityStatus.FAILED) {
        // The span is required: Tooltip injects pointer handlers into its child, and StatusLabel does not forward props.
        return (
            <Tooltip title={failReason} shown={!!failReason}>
                <span style={failReason ? { cursor: 'pointer' } : undefined}>
                    <StatusLabel
                        icon={<Icon name={ICONS.CROSS_MARK_CIRCLE_FILLED} size="16" intent={INTENT_TYPE.DANGER} />}
                        label={<Translate content="highAvailability.activities.processes.status.failed" />}
                    />
                </span>
            </Tooltip>
        );
    }

    if (status === HighAvailabilityEntityStatus.CANCELLED) {
        return (
            <StatusLabel
                icon={<span style={{ color: COLORS.GREY_70 }}><HaCancelledIcon width={15} height={15} /></span>}
                label={<Translate content="highAvailability.activities.processes.status.cancelled" />}
            />
        );
    }

    return (
        <StatusLabel
            icon={<span style={{ color: COLORS.INFO }}><HaPendingIcon width={15} height={15} /></span>}
            label={<Translate content="highAvailability.activities.processes.status.pending" />}
            subtitle={<Translate content="highAvailability.activities.processes.status.pendingEvacuationSubtitle" />}
        />
    );
};
